import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Compass } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { toast } from "sonner";
import { StyleQuiz, SkillCheck } from "@/components/assessment/CheckInSteps";
import { GrowthView } from "@/components/assessment/GrowthView";

const LVL = { below: "Let's build this up", on: "Right on track", above: "Ready for a challenge" };

const Results = ({ a, growth }) => (
  <div className="rise" data-testid="checkin-results">
    <Sparkles className="w-10 h-10 text-[#C87D53]" />
    <h2 className="font-display text-4xl text-[#2C4A3E] mt-3">All done. Thank you!</h2>
    <p className="text-stone-600 mt-2 max-w-xl">Your lessons and daily plan will now fit the way you learn. You can always grow, and every day is a new start.</p>
    <div className="grid sm:grid-cols-2 gap-3 mt-8">{Object.entries(a.subjects).map(([sid, s]) => (
      <div key={sid} className="rounded-2xl bg-white border border-[#E6DEC3] p-4" data-testid={`checkin-result-${sid}`}>
        <p className="text-xs uppercase tracking-widest text-stone-500">{s.name}</p><p className="font-display text-xl text-[#2C4A3E] mt-1">{LVL[s.level] || "In progress"}</p>
      </div>
    ))}</div>
    <GrowthView growth={growth} testId="checkin-growth" />
    <Link to="/planner" data-testid="checkin-to-plan" className="inline-block mt-8 px-7 py-3 rounded-full bg-[#2C4A3E] text-white">See my plan</Link>
  </div>
);

export default function CheckIn() {
  const { buddy } = useProfile();
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);
  const load = () => http.get("/assessment/me").then((r) => { setD(r.data); setA(r.data.needed && r.data.term === "winter" && r.data.assessment?.term !== "winter" ? null : r.data.assessment); });
  useEffect(() => { load(); }, []);
  const start = async () => { try { setA((await http.post("/assessment/start")).data); } catch (e) { toast.error(formatApiError(e)); } };
  const refresh = () => http.get("/assessment/me").then((r) => { setD(r.data); setA(r.data.assessment); });
  if (!d) return <div className="py-20 text-center text-stone-500">Loading…</div>;
  const voice = buddy?.voice;
  const winter = d.term !== "fall";
  const termName = d.term === "spring" ? "End-of-year" : "Mid-year";
  const nextSubject = a && Object.entries(a.subjects).find(([, s]) => !s.level);
  let body;
  if (!a) body = (
    <div className="rise" data-testid="checkin-intro">
      <Compass className="w-10 h-10 text-[#C87D53]" />
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-3">{winter ? `${termName} check-in` : "Start-of-year check-in"}</h1>
      <p className="text-stone-600 mt-4 max-w-xl">{winter ? <>{d.term === "spring" ? "You made it through the year!" : "Halfway through the year!"} Take a quick skill check in Math, English, Science and Social Studies so you can see how much you've grown since the fall.</> : <>Let's get to know you! First a few fun questions about <b>how you learn</b>, then a short skill check in Math, English, Science and Social Studies. It's not a test you can fail. It just helps {buddy?.name || "your Buddy"} make every lesson fit you.</>}</p>
      <p className="text-sm text-stone-500 mt-2">{winter ? "About 8 minutes" : "About 15 minutes"} · {d.school_year} school year</p>
      <button onClick={start} data-testid="checkin-start-btn" className="mt-8 px-8 py-3.5 rounded-full bg-[#C87D53] text-white text-lg">Let's begin</button>
    </div>
  );
  else if (a.status === "done") body = <Results a={a} growth={d.growth} />;
  else if (!a.style) body = <StyleQuiz quiz={d.style_quiz} interests={d.interests} readAloud={d.read_aloud} voice={voice} onDone={setA} />;
  else if (nextSubject) body = <SkillCheck key={nextSubject[0]} sid={nextSubject[0]} name={nextSubject[1].name} readAloud={d.read_aloud} voice={voice} onDone={refresh} />;
  else body = <p className="text-stone-500">Finishing up…</p>;
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="checkin-page">
      {a && a.status !== "done" && (
        <div className="flex gap-2 mb-10" data-testid="checkin-progress">
          {[...(a.term !== "fall" ? [] : [["style", "How I learn", !!a.style]]), ...Object.entries(a.subjects).map(([sid, s]) => [sid, s.name, !!s.level])].map(([k, l, ok]) => (
            <span key={k} className={`flex-1 text-center text-[11px] py-1.5 rounded-full ${ok ? "bg-[#6B9080] text-white" : "bg-[#F1EADB] text-stone-500"}`}>{l}</span>
          ))}
        </div>
      )}
      {body}
    </div>
  );
}
